import { EndNotes } from "@/components/EndNotes";
import { Menu } from "@/components/Menu";
import { MenuIcon } from "@/components/MenuIcon";
import { useUrls } from "@/hooks/useUrls";
import { Map } from "./Map";

import styles from "./styles.module.scss";

export function App() {
  const urls = useUrls();

  return (
    <main className={styles.container}>
      <div className={styles.inner}>
        <div className={styles.inner2}>
          <svg display="none" width="0" height="0">
            <defs>
              <symbol id="play" viewBox="0 0 15 19">
                <path
                  d="M0.779153 0.220863L14.1599 9.15999L0.779152 18.0991L0.779153 0.220863Z"
                  fill="currentColor"
                />
              </symbol>
              <symbol id="pause" viewBox="0 0 15 15">
                <rect x="1" width="4" height="15" fill="currentColor" />
                <rect x="9" width="4" height="15" fill="currentColor" />
              </symbol>
            </defs>
          </svg>

          <section>
            <header className={styles.header}>
              <h1 className={styles["header-title"]}>Global Trade Explorer</h1>

              <p className={styles["header-subtitle"]}>
                Visualize flows of trade around the&nbsp;world
              </p>

              <div className={styles["header-text"]}>
                <p>
                  To navigate a more complex and challenging era requires deeper
                  understanding of the full picture of the evolution of global
                  trade flows and the interdependencies and networks created by
                  them. Examining the diverse spectrum of global trade flows
                  from various perspectives, it is clear that global connections
                  are constantly evolving and reconfiguring. Enter our digital
                  experience to explore how these relationships are changing in
                  detail.
                </p>

                <p className={styles["header-cta"]}>
                  <Menu
                    className={styles["menu-btn"]}
                    data-layer-region="hero"
                    data-layer-text="Browse all visualizations"
                  >
                    <MenuIcon />
                    Browse&nbsp;<span>all visualizations</span>
                  </Menu>
                </p>
              </div>
            </header>

            <Map />

            <EndNotes mode="collapsed" isDarkBG={true}>
              <ol>
                <li>
                  The United Nations reports that the status of borders in this
                  area has not been agreed upon by the parties.
                </li>
              </ol>
              <p>
                The boundaries and names shown on this map do not imply official
                endorsement or acceptance by McKinsey &amp; Company. This map is
                not globally comprehensive and has been stylized to simplify
                some international borders and omit some smaller islands. Europe
                30 is treated as a single trading partner, including trade
                between individual Europe 30 economies and the rest of the
                Europe 30 bloc. Instances where the largest trading partner of
                an economy is self-reported as unspecified, are labeled “no data
                available.”
              </p>
              <p>Source: UN Comtrade (Refreshed 2025-01-30); UN Geospatial</p>
            </EndNotes>
          </section>

          <section>
            <div className={styles["start-container"]}>
              <h2 className={styles["start-heading"]}>
                Explore the data of global trade
              </h2>
              <ul className={styles["start-list"]}>
                {Object.entries(urls)
                  .filter(([key]) => key !== "home")
                  .map(([key, link]) => {
                    return (
                      <li key={key} className={styles["start-item"]}>
                        <a
                          className={styles["start-link"]}
                          href={link.url}
                          data-layer-event-prefix="Navigation Link"
                          data-layer-action="click"
                          data-layer-text={link.headline}
                          data-layer-region="keep exploring"
                        >
                          <div className={styles["start-image-wrap"]}>
                            <img src={link.thumbnailDark} alt={link.headline} />
                          </div>
                          <p className={styles["start-link-text"]}>
                            {link.headline}
                          </p>
                        </a>
                      </li>
                    );
                  })}
              </ul>
            </div>
          </section>

          <EndNotes mode="no-heading" isDarkBG={true}>
            <p>
              Information about the visualization data: Trade values shown are
              based on self-reported trade values to UN Comtrade. The accuracy
              of the data is based on each economy’s reported values. Sector
              taxonomy and classification are based on the judgment of several
              industry experts working with McKinsey Global Institute. There are
              bilateral asymmetries in import and export datasets from UN
              Comtrade. Data used in the visualization is from or ends in 2023.
              McKinsey & Company expects to update the data as 2024 data becomes
              available but is under no obligation to update the data or correct
              the results presented in this visualization.
            </p>
          </EndNotes>
        </div>
      </div>
    </main>
  );
}
